import { Alert, Stack } from '@mui/material';
import { PhaseLabel } from '@vy/types';
import { isNotEmptyString, vyDate, VyDate } from '@vy/utils';
import { whenDefined } from '@vy/null';
import { getLastContractDay } from '@vy/services';
import { useMemo } from 'react';
import { Control, Controller, useWatch, UseFormSetValue } from 'react-hook-form';
import { VyTextInput, VySelect } from '../../../../../components/new-design/atoms';
import { VyDatePicker } from '../../../../../components/new-design/date-picker';
import { VyCheckbox } from '../../../../../components/new-design/inputs/VyCheckbox';
import { useGetPhaseLabels } from '../../../../../hooks/requests/use-phase-labels';
import { NewPhaseForm as NewPhaseFormType } from '../types/new-phase-form-schema';
import { getLabelIcon } from '../contract-content-utils/get-phase-label-icon';
import { PhaseLabelChip } from './PhaseLabelChip';
import { VyIcon } from '@vy/ui-design-system';

type NewPhaseFormProps = {
  minDate: VyDate;
  maxDate?: VyDate;
  control: Control<NewPhaseFormType>;
  setValue: UseFormSetValue<NewPhaseFormType>;
  planDuration: number;
  coTermDisabled: boolean;
  nameInputLabel?: string;
  dateInputLabel?: string;
};

export const NewPhaseForm: React.FC<NewPhaseFormProps> = ({
  control,
  setValue,
  planDuration,
  coTermDisabled,
  minDate,
  maxDate,
  nameInputLabel = 'Name this phase (Optional)',
  dateInputLabel = 'Phase effective as of:',
}) => {
  const phaseLabelsResponse = useGetPhaseLabels();
  const { phaseLabels = [] } = phaseLabelsResponse ?? {};

  const phaseLabelsSelectItems = useMemo(
    () => phaseLabels.map(convertPhaseLabelsToSelectItems),
    [phaseLabels],
  );

  const coTerm = useWatch({ control, name: 'coTerm' });
  const effectiveDate = useWatch({ control, name: 'effectiveDate' });

  const handleCoTermToggle = (checked: boolean, onChange: (value: boolean) => void) => {
    onChange(checked);

    if (checked) return;

    const defaultEndDate = whenDefined(effectiveDate, (date) =>
      getLastContractDay(vyDate(date), planDuration)?.toDate(),
    );
    setValue('newContractEndDate', defaultEndDate, { shouldValidate: true });
  };

  return (
    <Stack
      sx={{
        flexDirection: "column",
        gap: 2
      }}>
      <Controller
        control={control}
        name="effectiveDate"
        render={({ field, formState: { errors } }) => (
          <VyDatePicker
            label={dateInputLabel}
            {...field}
            minDate={minDate}
            maxDate={maxDate}
            placeholder="Select a date"
            size="medium"
            error={isNotEmptyString(errors.effectiveDate?.message)}
            customBottomContent={
              <Alert
                variant="filled"
                severity="warning"
                icon={<VyIcon icon="Warning" />}
                sx={{
                  background: 'none',
                  boxShadow: 'none',
                  fontSize: '12px',
                  color: 'grey',
                  padding: 0,
                  '& .MuiAlert-message': {
                    maxWidth: '245px',
                  },
                }}
              >
                Date selected must be after the previous phase’s start.
              </Alert>
            }
          />
        )}
      />
      <Stack
        sx={{
          flexDirection: "row",
          gap: 2
        }}>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <VyTextInput label={nameInputLabel} {...field} fullWidth placeholder="Enter name" />
          )}
        />
        <Controller
          control={control}
          name="label"
          render={({ field: { value, onChange }, formState: { errors } }) => {
            const selectedOption = phaseLabelsSelectItems.find((item) => item.key === value);

            return (
              <VySelect
                fullWidth
                selectedOption={selectedOption}
                label="Label"
                options={phaseLabelsSelectItems}
                onSelectionChange={(option) => onChange(option?.key)}
                render={(props, context) => (
                  <PhaseLabelChip {...props} type={props.key} editable={context === 'menuItem'} />
                )}
                keyExtractor={({ key }) => key}
                error={isNotEmptyString(errors.label?.message)}
              />
            );
          }}
        />
      </Stack>

      <Controller
        control={control}
        name="coTerm"
        render={({ field: { value, onChange } }) => (
          <VyCheckbox
            checked={value}
            disabled={coTermDisabled}
            onChange={(event) => handleCoTermToggle(event.target.checked, onChange)}
            label="Co-term"
          />
        )}
      />

      {!coTerm && (
        <Controller
          control={control}
          name="newContractEndDate"
          render={({ field, formState: { errors } }) => (
            <VyDatePicker
              label="New contract end date"
              {...field}
              minDate={whenDefined(effectiveDate, (date) => vyDate(date).add(1, 'day'))}
              placeholder="Select a date"
              size="medium"
              error={isNotEmptyString(errors.newContractEndDate?.message)}
              helperText={errors.newContractEndDate?.message}
            />
          )}
        />
      )}
    </Stack>
  );
};

function convertPhaseLabelsToSelectItems({ type, textLabel, color, backgroundColor }: PhaseLabel) {
  const icon = getLabelIcon(type);

  return {
    color,
    backgroundColor,
    icon,
    key: type,
    label: textLabel,
  };
}
